// src/components/dashboard/ChatWindow.jsx
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  PaperClipIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import {
  getChatContacts,
  getChatMessages,
  sendChatMessage,
  markMessagesAsRead,
  uploadChatAttachment,
  deleteChatAttachment,
  getOneDriveMetadata,
  getGoogleDriveMetadata
} from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { toast } from 'react-hot-toast';

// Helper function to decode HTML entities
function decodeHtmlEntities(text) {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

// Smart date formatting with i18n
function formatDateSeparator(timestamp, t, locale) {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  
  if (messageDate.getTime() === todayDate.getTime()) {
    return t('chat.dates.today');
  } else if (messageDate.getTime() === yesterdayDate.getTime()) {
    return t('chat.dates.yesterday');
  } else {
    const diffTime = todayDate.getTime() - messageDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays < 7) {
      return date.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-US', { weekday: 'long' });
    }
    
    return date.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
}

// Get date key for grouping messages
function getDateKey(timestamp) {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

// Smart Link component with i18n
const SmartLink = ({ url, isYourMessage, type = 'generic', t }) => {
  const [displayName, setDisplayName] = useState(() => {
    if (type === 'onedrive') return t('chat.links.onedrive');
    if (type === 'google-doc') return t('chat.links.googleDoc');
    if (type === 'google-sheet') return t('chat.links.googleSheet');
    if (type === 'google-presentation') return t('chat.links.googlePresentation');
    if (type === 'google-drive') return t('chat.links.googleDrive');
    if (type === 'generic') return url.length > 100 ? url.substring(0, 97) + '...' : url;
    return url;
  });
  
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    if (type === 'onedrive' && (url.includes('1drv.ms') || url.includes('1drv.com'))) {
      setIsLoading(true);
      
      getOneDriveMetadata(url)
        .then(response => {
          if (response.success && response.data) {
            if (response.data.requiresAuth) {
              setDisplayName(`${t('chat.links.private')} ${response.data.filename || t('chat.links.onedrive')}`);
            } else {
              setDisplayName(response.data.filename || t('chat.links.onedrive'));
            }
          }
        })
        .catch(err => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
    
    if (type.startsWith('google-') && (url.includes('docs.google.com') || url.includes('drive.google.com'))) {
      setIsLoading(true);
      
      getGoogleDriveMetadata(url)
        .then(response => {
          if (response.success && response.data?.filename) {
            setDisplayName(response.data.filename);
          }
        })
        .catch(err => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [url, type, t]);
  
  const linkColorClass = isYourMessage 
    ? 'text-cyan-100 hover:text-white' 
    : 'text-blue-600 hover:text-blue-800';
  
  if (type === 'onedrive') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 ${linkColorClass} hover:underline transition-all`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
        </svg>
        <span className={`font-medium ${isLoading ? 'opacity-60' : ''}`}>
          {displayName}
        </span>
        {isLoading && (
          <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        )}
      </a>
    );
  }
  
  if (type.startsWith('google-')) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 ${linkColorClass} hover:underline transition-all`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
        </svg>
        <span className={`font-medium ${isLoading ? 'opacity-60' : ''}`}>
          {displayName}
        </span>
        {isLoading && (
          <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        )}
      </a>
    );
  }
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 ${linkColorClass} underline break-all`}
      onClick={(e) => e.stopPropagation()}
    >
      <LinkIcon className="w-4 h-4 flex-shrink-0" />
      <span>{displayName}</span>
    </a>
  );
};

// Helper function to detect and format links
function formatMessageWithLinks(text, isYourMessage = false, t) {
  if (!text) return text;
  
  const decodedText = decodeHtmlEntities(text);
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  
  const parts = decodedText.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      const lowerUrl = part.toLowerCase();
      
      if (lowerUrl.includes('1drv.ms') || 
          lowerUrl.includes('1drv.com') ||
          lowerUrl.includes('onedrive.live.com') ||
          lowerUrl.includes('sharepoint.com')) {
        return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="onedrive" t={t} />;
      }
      
      if (lowerUrl.includes('docs.google.com')) {
        if (lowerUrl.includes('/document/')) {
          return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-doc" t={t} />;
        }
        if (lowerUrl.includes('/spreadsheets/')) {
          return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-sheet" t={t} />;
        }
        if (lowerUrl.includes('/presentation/')) {
          return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-presentation" t={t} />;
        }
      }
      
      if (lowerUrl.includes('drive.google.com')) {
        return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-drive" t={t} />;
      }
      
      return <SmartLink key={index} url={part} isYourMessage={isYourMessage} type="generic" t={t} />;
    }
    
    return <span key={index}>{part}</span>;
  });
}

// Helper function to format time in 24h format
function formatTime24h(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

function ChatWindow({ 
  isOpen, 
  onClose, 
  currentUser, 
  onUnreadCountChange,
  markMessagesAsReadForUser,
  clearUserFromSessionTracking
}) {
  const { t, i18n } = useTranslation();
  const { sendMessage: wsSendMessage, addMessageHandler, isConnected } = useWebSocket();
  const { playDirectMessage } = useSoundEffects();
  
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [showUserList, setShowUserList] = useState(true);
  
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Pagination states
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [oldestMessageId, setOldestMessageId] = useState(null);

  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sendMessageError, setSendMessageError] = useState('');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const inputBarRef = useRef(null);

  // iOS Safari fix: flex-1 inside nested flex parents doesn't give iOS a
  // concrete pixel height, so overflow-y:auto never scrolls.
  // We measure the dropZone height minus the input bar height and set it
  // explicitly on the messages container. ResizeObserver keeps it in sync
  // when the keyboard appears/disappears or the window resizes.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const dropZone = dropZoneRef.current;
    if (!container || !dropZone) return;

    const setHeight = () => {
      const dropZoneHeight = dropZone.getBoundingClientRect().height;
      const inputBar = inputBarRef.current;
      const inputBarHeight = inputBar ? inputBar.getBoundingClientRect().height : 0;
      const available = dropZoneHeight - inputBarHeight;
      if (available > 0) {
        container.style.height = `${available}px`;
        container.style.flex = 'none';
      }
    };

    setHeight();

    const ro = new ResizeObserver(setHeight);
    ro.observe(dropZone);
    if (inputBarRef.current) ro.observe(inputBarRef.current);

    return () => ro.disconnect();
  }, []);
  
  const MESSAGE_CHAR_LIMIT = 1000;
  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const MESSAGES_PER_PAGE = 50;
  
  const hasLoadedContactsRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const currentUserIdRef = useRef(currentUser?.id || currentUser?.userId);
  const selectedUserRef = useRef(selectedUser);
  const previousSelectedUserRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const messagesFetchTriggerRef = useRef(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    currentUserIdRef.current = currentUser?.id || currentUser?.userId;
  }, [currentUser]);

  useEffect(() => {
    previousSelectedUserRef.current = selectedUserRef.current;
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // Document-level drag and drop prevention
  useEffect(() => {
    if (!isOpen || !selectedUser) return;
    
    const isMobile = window.innerWidth < 640;
    if (isMobile && showUserList) return;

    const preventBrowserDefault = (e) => {
      const dropZone = dropZoneRef.current;
      if (dropZone && dropZone.contains(e.target)) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('dragover', preventBrowserDefault, false);
    document.addEventListener('drop', preventBrowserDefault, false);

    return () => {
      document.removeEventListener('dragover', preventBrowserDefault, false);
      document.removeEventListener('drop', preventBrowserDefault, false);
    };
  }, [isOpen, showUserList, selectedUser]);

  // NATIVE DRAG AND DROP HANDLERS
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    
    const isMobile = window.innerWidth < 640;
    const shouldSkip = !dropZone || !selectedUser || (isMobile && showUserList);
    
    if (shouldSkip) return;

    const handleNativeDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleNativeDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    };

    const handleNativeDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dropZone.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleNativeDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      
      const file = files[0];
      
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        const errorMsg = t('chat.errors.fileTooLarge', { size: sizeMB });
        setSendMessageError(errorMsg);
        toast.error(t('chat.toast.fileTooLarge'));
        return;
      }
      
      setAttachedFile(file);
      setSendMessageError('');
      toast.success(t('chat.toast.fileReady', { name: file.name }));
    };

    dropZone.addEventListener('dragenter', handleNativeDragEnter, false);
    dropZone.addEventListener('dragover', handleNativeDragOver, false);
    dropZone.addEventListener('dragleave', handleNativeDragLeave, false);
    dropZone.addEventListener('drop', handleNativeDrop, false);

    return () => {
      dropZone.removeEventListener('dragenter', handleNativeDragEnter, false);
      dropZone.removeEventListener('dragover', handleNativeDragOver, false);
      dropZone.removeEventListener('dragleave', handleNativeDragLeave, false);
      dropZone.removeEventListener('drop', handleNativeDrop, false);
    };
  }, [selectedUser, showUserList, MAX_FILE_SIZE, isOpen, t]);

  // Scroll handler for pagination + isAtBottom tracking
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop } = messagesContainerRef.current;
    
    // column-reverse: scrollTop 0 = visual bottom, negative = scrolled up
    isAtBottomRef.current = Math.abs(scrollTop) < 50;
    
    if (isLoadingMoreMessages || !hasMoreMessages) return;
    
    if (scrollTop < 50) {
      loadMoreMessages();
    }
  }, [isLoadingMoreMessages, hasMoreMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMoreMessages || !hasMoreMessages || !selectedUser || !oldestMessageId) return;
    
    isLoadingMoreRef.current = true;
    setIsLoadingMoreMessages(true);
    
    try {
      const container = messagesContainerRef.current;
      if (!container) return;
      
      const scrollHeightBefore = container.scrollHeight;
      const scrollTopBefore = container.scrollTop;
      
      const response = await getChatMessages(selectedUser.id, MESSAGES_PER_PAGE, oldestMessageId);
      
      if (response.success && Array.isArray(response.data)) {
        const messages = response.data;
        
        if (messages.length === 0) {
          setHasMoreMessages(false);
          return;
        }
        
        const messagesWithFormattedTime = messages.map(msg => ({
          ...msg,
          message: msg.message || msg.messageContent || '',
          time: formatTime24h(msg.timestamp || msg.sent_at)
        }));
        
        setChatMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = messagesWithFormattedTime.filter(m => !existingIds.has(m.id));
          
          if (newMessages.length === 0) {
            setHasMoreMessages(false);
            return prev;
          }
          
          return [...newMessages, ...prev];
        });
        
        setHasMoreMessages(response.hasMore || messages.length === MESSAGES_PER_PAGE);
        
        if (messagesWithFormattedTime.length > 0) {
          const oldestNewMsg = messagesWithFormattedTime[messagesWithFormattedTime.length - 1];
          setOldestMessageId(oldestNewMsg.id);
        }
        
        requestAnimationFrame(() => {
          if (container) {
            const scrollHeightAfter = container.scrollHeight;
            const addedHeight = scrollHeightAfter - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + addedHeight;
          }
        });
      }
    } catch (err) {
      console.error('Failed to load more messages:', err);
      toast.error(t('chat.toast.loadOlderFailed'));
    } finally {
      setIsLoadingMoreMessages(false);
      setTimeout(() => {
        isLoadingMoreRef.current = false;
      }, 500);
    }
  }, [isLoadingMoreMessages, hasMoreMessages, selectedUser, oldestMessageId, t]);

  useEffect(() => {
    if (!isOpen || !isConnected) return;
    
    const handleChatMessage = async (data) => {
      const senderId = data.senderId || data.sender_id || data.sender_user_id;
      const receiverId = data.receiverId || data.receiver_id || data.receiver_user_id;
      const currentUserId = currentUserIdRef.current;
      
      if (String(senderId) !== String(currentUserId) && String(receiverId) !== String(currentUserId)) {
        return;
      }
      
      const isIncomingMessage = String(senderId) !== String(currentUserId);
      const relevantUserId = isIncomingMessage ? senderId : receiverId;
      
      // Play notification sound for incoming personal messages
      if (isIncomingMessage) {
        playDirectMessage();
      }
      
      if (String(selectedUserRef.current?.id) === String(relevantUserId)) {
        const messageContent = data.message || data.messageContent || '';
        const newMessage = {
          id: data.messageId || data.id || `ws-${Date.now()}-${Math.random()}`,
          senderUserId: senderId,
          sender: isIncomingMessage ? 'them' : 'you',
          message: decodeHtmlEntities(messageContent),
          time: formatTime24h(data.timestamp || data.created_at || new Date()),
          timestamp: data.timestamp || data.created_at || new Date().toISOString(),
          attachment: data.attachment || null,
          status: data.status || 'delivered'
        };
        
        setChatMessages(prev => {
          const exists = prev.some(msg => 
            msg.id === newMessage.id || 
            (Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000 && 
             msg.message === newMessage.message &&
             String(msg.senderUserId) === String(newMessage.senderUserId))
          );
          if (exists) return prev;
          return [...prev, newMessage];
        });
        
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        });
        
        if (isIncomingMessage && isOpenRef.current) {
          try {
            await markMessagesAsRead(relevantUserId);
            
            if (window.markMessagesReadGlobally) {
              window.markMessagesReadGlobally(String(relevantUserId));
            }
            
            setContacts(prev => prev.map(contact => 
              String(contact.id) === String(relevantUserId) 
                ? { ...contact, unread: 0 }
                : contact
            ));
            
            setFilteredContacts(prev => prev.map(contact => 
              String(contact.id) === String(relevantUserId) 
                ? { ...contact, unread: 0 }
                : contact
            ));
            
          } catch (err) {
            console.error('Failed to mark as read:', err);
          }
        }
      } else {
        const isIncomingMessage = String(senderId) !== String(currentUserId);
        
        if (isIncomingMessage) {
          const updateContact = (contact) => {
            if (String(contact.id) === String(senderId)) {
              return {
                ...contact,
                lastMessageTime: formatTime24h(data.timestamp || data.created_at || new Date()),
                unread: (contact.unread || 0) + 1
              };
            }
            return contact;
          };
          
          setContacts(prev => prev.map(updateContact));
          setFilteredContacts(prev => prev.map(updateContact));
        }
      }
    };
    
    const unsubscribeDirect = addMessageHandler('direct_message', (data) => {
      const messageData = data.message || {};
      
      const normalizedMessage = {
        messageId: messageData.id,
        senderId: data.senderId,
        receiverId: data.receiverId,
        message: messageData.message || messageData.messageContent || '',
        timestamp: messageData.created_at || data.timestamp,
        attachment: messageData.attachment,
        status: 'delivered'
      };
      
      if (!normalizedMessage.receiverId) {
        const currentUserId = currentUserIdRef.current;
        if (String(normalizedMessage.senderId) !== String(currentUserId)) {
          normalizedMessage.receiverId = currentUserId;
        }
      }
      
      handleChatMessage(normalizedMessage);
    });
    
    const unsubscribeChat = addMessageHandler('chat_message', handleChatMessage);
    const unsubscribeNew = addMessageHandler('new_message', (data) => {
      if (data.chatType === 'normal' || !data.sessionId) {
        handleChatMessage(data);
      }
    });
    
    return () => {
      unsubscribeDirect();
      unsubscribeChat();
      unsubscribeNew();
    };
  }, [selectedUser, isOpen, isConnected, addMessageHandler, playDirectMessage]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter(contact => 
        contact.name.toLowerCase().includes(query)
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const scrollToBottom = useCallback((force = false) => {
    if (!force && !isAtBottomRef.current) return;
    // Now that messagesContainerRef has an explicit pixel height (set by
    // the ResizeObserver useLayoutEffect above), scrollTop assignment is
    // reliable on iOS Safari. Still use double rAF to ensure messages are
    // painted before we measure scrollHeight.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });
  }, []);

  useEffect(() => {
    if (chatMessages.length > 0 && !isLoadingMessages && !isLoadingMoreRef.current) {
      scrollToBottom(true);
    }
  }, [chatMessages, isLoadingMessages, scrollToBottom]);

  const handleFileSelect = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setSendMessageError(t('chat.errors.fileTooLarge', { size: (file.size / 1024 / 1024).toFixed(2) }));
      toast.error(t('chat.toast.fileTooLarge'));
      return;
    }
    setAttachedFile(file);
    setSendMessageError('');
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeAttachedFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('chat.toast.fileTooLarge'));
          return;
        }
        setAttachedFile(file);
        return;
      }
    }
  }, [MAX_FILE_SIZE, t]);

  useEffect(() => {
    window.refreshChatWindow = () => {
      if (hasLoadedContactsRef.current) {
        getChatContacts().then(response => {
          if (response.success && Array.isArray(response.data)) {
            setContacts(response.data);
            setFilteredContacts(response.data);
          }
        });
      }
    };
    
    return () => {
      delete window.refreshChatWindow;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    
    const userId = currentUser?.id || currentUser?.userId;
    if (!userId) return;
    
    if (hasLoadedContactsRef.current) return;

    const fetchContacts = async () => {
      setIsLoadingContacts(true);
      setContactsError('');
      try {
        const response = await getChatContacts();
        
        if (response.success && Array.isArray(response.data)) {
          setContacts(response.data);
          setFilteredContacts(response.data);
          hasLoadedContactsRef.current = true;
          
          if (response.data.length > 0) {
            const firstUnread = response.data.find(c => c.unread > 0);
            handleUserSelect(firstUnread || response.data[0]);
          }
        } else {
          setContactsError(response.message || t('chat.errors.loadContactsFailed'));
        }
      } catch (err) {
        console.error('Fetch contacts error:', err);
        setContactsError(err.message || t('chat.errors.loadContactsError'));
      } finally {
        setIsLoadingContacts(false);
      }
    };

    fetchContacts();
  }, [isOpen, currentUser, t]);

  useEffect(() => {
    if (!isOpen) {
      if (selectedUserRef.current && window.markMessagesReadGlobally) {
        window.markMessagesReadGlobally(String(selectedUserRef.current.id));
      }
      
      if (previousSelectedUserRef.current && window.markMessagesReadGlobally && 
          previousSelectedUserRef.current.id !== selectedUserRef.current?.id) {
        window.markMessagesReadGlobally(String(previousSelectedUserRef.current.id));
      }
      
      if (clearUserFromSessionTracking) {
        if (selectedUserRef.current) {
          clearUserFromSessionTracking(String(selectedUserRef.current.id));
        }
        if (previousSelectedUserRef.current && 
            previousSelectedUserRef.current.id !== selectedUserRef.current?.id) {
          clearUserFromSessionTracking(String(previousSelectedUserRef.current.id));
        }
      }
      
      setShowUserList(true);
      hasLoadedContactsRef.current = false;
      setTimeout(() => {
        if (!isOpenRef.current) {
          setContacts([]);
          setFilteredContacts([]);
          setSelectedUser(null);
          setChatMessages([]);
          setMessageInput('');
          setSendMessageError('');
          setSearchQuery('');
          removeAttachedFile();
          setHasMoreMessages(false);
          setOldestMessageId(null);
        }
      }, 300);
    }
  }, [isOpen, clearUserFromSessionTracking]);

  useEffect(() => {
    if (!selectedUser || !isOpen) {
      if (!selectedUser) {
        setChatMessages([]);
        setHasMoreMessages(false);
        setOldestMessageId(null);
      }
      return;
    }

    const userId = currentUserIdRef.current;
    if (!userId) return;

    if (window.markMessagesReadGlobally) {
      window.markMessagesReadGlobally(String(selectedUser.id));
    }

    const fetchMessages = async () => {
      setIsLoadingMessages(true);
      setMessagesError('');
      try {
        const response = await getChatMessages(selectedUser.id, MESSAGES_PER_PAGE);
        if (response.success && Array.isArray(response.data)) {
          const messagesWithFormattedTime = response.data.map(msg => ({
            ...msg,
            message: msg.message || msg.messageContent || '',
            time: formatTime24h(msg.timestamp || msg.sent_at)
          }));
          
          setChatMessages(messagesWithFormattedTime);
          setHasMoreMessages(response.hasMore || messagesWithFormattedTime.length === MESSAGES_PER_PAGE);
          
          if (messagesWithFormattedTime.length > 0) {
            const oldestMsg = messagesWithFormattedTime[messagesWithFormattedTime.length - 1];
            setOldestMessageId(oldestMsg.id);
          }

          try {
            await markMessagesAsRead(selectedUser.id);
            
            if (window.markMessagesReadGlobally) {
              window.markMessagesReadGlobally(String(selectedUser.id));
            }
            
            setContacts(prevContacts =>
              prevContacts.map(c =>
                c.id === selectedUser.id ? { ...c, unread: 0 } : c
              )
            );
            
            setFilteredContacts(prevContacts =>
              prevContacts.map(c =>
                c.id === selectedUser.id ? { ...c, unread: 0 } : c
              )
            );
            
          } catch (readError) {
            console.error('Failed to mark messages as read:', readError);
          }
        } else {
          setMessagesError(response.message || t('chat.errors.loadMessagesFailed'));
        }
      } catch (err) {
        setMessagesError(err.message || t('chat.errors.loadMessagesError'));
      } finally {
        setIsLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedUser?.id, isOpen, messagesFetchTriggerRef.current, t]);

  const handleUserSelect = (user) => {
    if (selectedUser?.id === user.id) {
      setChatMessages([]);
      setMessagesError('');
      setSendMessageError('');
      setHasMoreMessages(false);
      setOldestMessageId(null);
      messagesFetchTriggerRef.current += 1;
      
      if (window.innerWidth < 640) {
        setShowUserList(false);
      }
      return;
    }

    setSelectedUser(user);
    setChatMessages([]);
    setMessagesError('');
    setSendMessageError('');
    setHasMoreMessages(false);
    setOldestMessageId(null);
    
    if (window.innerWidth < 640) {
      setShowUserList(false);
    }
  };

  const handleBackToUsers = () => setShowUserList(true);

  // Scroll to bottom when mobile chat view opens (after user selects a contact)
  // This is specifically for mobile where the view transition can delay rendering
  useEffect(() => {
    // Only trigger when mobile view is showing chat (not user list)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    if (isMobile && !showUserList && selectedUser && chatMessages.length > 0 && !isLoadingMessages) {
      // Extra delay for mobile to ensure DOM is fully ready after transition
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = messagesContainerRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          });
        });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [showUserList, selectedUser, chatMessages.length, isLoadingMessages]);

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !attachedFile) || !selectedUser) return;

    const userId = currentUserIdRef.current;
    if (!userId) return;

    setIsSendingMessage(true);
    setSendMessageError('');
    
    let attachmentData = null;
    const originalFileName = attachedFile?.name;
    
    if (attachedFile) {
      setIsUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('file', attachedFile);
        formData.append('receiverId', selectedUser.id);
        
        const uploadResponse = await uploadChatAttachment(formData, (progress) => {
          setUploadProgress(progress);
        });
        
        if (uploadResponse.success) {
          let fileType = uploadResponse.data.fileType || attachedFile.type;
          
          const typeMapping = {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'application/docx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'application/xlsx',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'application/pptx',
            'application/vnd.ms-excel': 'application/excel',
            'application/vnd.ms-powerpoint': 'application/ppt',
            'application/vnd.ms-word': 'application/word',
            'application/vnd.oasis.opendocument.text': 'application/odt',
            'application/vnd.oasis.opendocument.spreadsheet': 'application/ods',
            'application/vnd.oasis.opendocument.presentation': 'application/odp'
          };
          
          fileType = typeMapping[fileType] || fileType;
          if (fileType.length > 50) {
            fileType = fileType.substring(0, 50);
          }
          
          attachmentData = {
            attachmentName: originalFileName,
            attachmentType: fileType,
            attachmentSizeKb: Math.round(uploadResponse.data.fileSize / 1024),
            attachmentUrl: uploadResponse.data.fileUrl
          };
        } else {
          setSendMessageError(uploadResponse.message || t('chat.errors.uploadFailed'));
          setIsSendingMessage(false);
          setIsUploadingFile(false);
          return;
        }
      } catch (uploadError) {
        setSendMessageError(t('chat.errors.uploadError'));
        setIsSendingMessage(false);
        setIsUploadingFile(false);
        return;
      } finally {
        setIsUploadingFile(false);
        setUploadProgress(0);
      }
    }
    
    const tempMessageId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempMessageId,
      senderUserId: userId,
      sender: 'you',
      message: messageInput,
      time: formatTime24h(new Date()),
      timestamp: new Date().toISOString(),
      status: 'sending',
      attachment: attachedFile ? {
        name: originalFileName,
        type: attachedFile.type,
        size: `${(attachedFile.size / 1024).toFixed(0)} KB`,
        url: attachmentData?.attachmentUrl
      } : null
    };

    setChatMessages(prevMessages => [...prevMessages, optimisticMessage]);
    scrollToBottom(true);
    const messageToSend = messageInput;
    setMessageInput('');
    removeAttachedFile();
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const messageData = {
        message: messageToSend,
        ...attachmentData
      };
      
      const response = await sendChatMessage(selectedUser.id, messageData);
      if (response.success && response.data) {
        setChatMessages(prevMessages =>
          prevMessages.map(msg => 
            msg.id === tempMessageId 
              ? { 
                  ...response.data, 
                  message: response.data.message || response.data.messageContent || messageToSend,
                  sender: 'you',
                  status: response.data.status || 'delivered',
                  time: formatTime24h(response.data.timestamp || response.data.sent_at),
                  attachment: response.data.attachment ? {
                    ...response.data.attachment,
                    name: originalFileName || response.data.attachment.name
                  } : null
                }
              : msg
          )
        );
        
        setContacts(prevContacts => {
          const updatedContacts = prevContacts.map(c =>
            c.id === selectedUser.id ? {
                ...c,
                lastMessageTime: formatTime24h(new Date()),
            } : c
          );
          return updatedContacts;
        });
        
        setFilteredContacts(prevContacts => {
          const updatedContacts = prevContacts.map(c =>
            c.id === selectedUser.id ? {
                ...c,
                lastMessageTime: formatTime24h(new Date()),
            } : c
          );
          return updatedContacts;
        });
        
      } else {
        setSendMessageError(response.message || t('chat.errors.sendFailed'));
        setChatMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempMessageId));
        setMessageInput(messageToSend);
        
        if (attachmentData?.attachmentUrl) {
          try {
            await deleteChatAttachment(attachmentData.attachmentUrl);
          } catch (deleteError) {}
        }
      }
    } catch (err) {
      setSendMessageError(err.message || t('chat.errors.sendError'));
      setChatMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempMessageId));
      setMessageInput(messageToSend);
    } finally {
      setIsSendingMessage(false);
      requestAnimationFrame(() => {
        if (textareaRef.current) textareaRef.current.focus();
      });
    }
  };

  const handleMessageInputChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= MESSAGE_CHAR_LIMIT) {
      setMessageInput(newValue);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSendingMessage && !isUploadingFile) {
        handleSendMessage();
      }
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const renderMessagesWithDates = () => {
    const elements = [];
    let lastDateKey = null;

    chatMessages.forEach((msg) => {
      const currentDateKey = getDateKey(msg.timestamp);
      
      if (currentDateKey !== lastDateKey) {
        elements.push(
          <div key={`date-${currentDateKey}`} className="flex items-center justify-center my-4">
            <div className="bg-slate-200 px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-slate-600">
                {formatDateSeparator(msg.timestamp, t, i18n.language)}
              </span>
            </div>
          </div>
        );
        lastDateKey = currentDateKey;
      }

      elements.push(
        <div key={msg.id} className={`flex ${msg.sender === 'you' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[70%] min-w-0 px-3.5 py-2.5 rounded-xl shadow-sm ${msg.sender === 'you' ? 'bg-cyan-500 text-white ml-10' : 'bg-white text-slate-700 border border-slate-200 mr-10'}`}>
            {msg.attachment && (
              <a
                href={msg.attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`mb-2 p-2 rounded-md ${msg.sender === 'you' ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-slate-100 hover:bg-slate-200'} flex items-center transition-colors`}
              >
                <DocumentTextIcon className={`w-5 h-5 mr-2 ${msg.sender === 'you' ? 'text-cyan-100' : 'text-slate-500'}`} />
                <div className="text-xs flex-1 min-w-0">
                  <p className={`font-medium truncate ${msg.sender === 'you' ? 'text-white' : 'text-slate-700'}`}>
                    {msg.attachment.name}
                  </p>
                  <p className={msg.sender === 'you' ? 'text-cyan-100' : 'text-slate-500'}>
                    {msg.attachment.size}
                  </p>
                </div>
              </a>
            )}
            <div className="text-sm whitespace-pre-wrap break-words">
              {formatMessageWithLinks(msg.message || msg.messageContent, msg.sender === 'you', t)}
            </div>
            <p className={`text-xs mt-1 ${msg.sender === 'you' ? 'text-cyan-100 text-right' : 'text-slate-400'}`}>
              {msg.time}
            </p>
          </div>
        </div>
      );
    });

    return elements;
  };

  const renderMessagesWithDatesReversed = () => {
    const elements = [];
    const reversedMessages = [...chatMessages].reverse();

    reversedMessages.forEach((msg, index) => {
      const currentDateKey = getDateKey(msg.timestamp);

      elements.push(
        <div key={msg.id} className={`flex ${msg.sender === 'you' ? 'justify-end' : 'justify-start'} mb-1`}>
          <div className={`max-w-[70%] min-w-0 px-3.5 py-2.5 rounded-xl shadow-sm ${msg.sender === 'you' ? 'bg-cyan-500 text-white ml-10' : 'bg-white text-slate-700 border border-slate-200 mr-10'}`}>
            {msg.attachment && (
              <a
                href={msg.attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`mb-2 p-2 rounded-md ${msg.sender === 'you' ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-slate-100 hover:bg-slate-200'} flex items-center transition-colors`}
              >
                <DocumentTextIcon className={`w-5 h-5 mr-2 ${msg.sender === 'you' ? 'text-cyan-100' : 'text-slate-500'}`} />
                <div className="text-xs flex-1 min-w-0">
                  <p className={`font-medium truncate ${msg.sender === 'you' ? 'text-white' : 'text-slate-700'}`}>
                    {msg.attachment.name}
                  </p>
                  <p className={msg.sender === 'you' ? 'text-cyan-100' : 'text-slate-500'}>
                    {msg.attachment.size}
                  </p>
                </div>
              </a>
            )}
            <div className="text-sm whitespace-pre-wrap break-words">
              {formatMessageWithLinks(msg.message || msg.messageContent, msg.sender === 'you', t)}
            </div>
            <p className={`text-xs mt-1 ${msg.sender === 'you' ? 'text-cyan-100 text-right' : 'text-slate-400'}`}>
              {msg.time}
            </p>
          </div>
        </div>
      );

      // Push date separator after the OLDEST message of each date group.
      // In column-reverse this places it visually ABOVE all messages for that day.
      const nextMsg = reversedMessages[index + 1];
      const nextDateKey = nextMsg ? getDateKey(nextMsg.timestamp) : null;

      if (currentDateKey !== nextDateKey) {
        elements.push(
          <div key={`date-${currentDateKey}`} className="flex items-center justify-center my-4">
            <div className="bg-slate-200 px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-slate-600">
                {formatDateSeparator(msg.timestamp, t, i18n.language)}
              </span>
            </div>
          </div>
        );
      }
    });

    return elements;
  };

  if (!isOpen) return null;

  const headerTitle = t('chat.title');

  const DesktopView = (
    <div 
      className="fixed bottom-6 right-10 z-50 hidden sm:flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" 
      style={{ width: '700px', height: '550px' }}
      data-chat-viewing-user-id={selectedUser?.id || ''}
    >
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-cyan-500 rounded-full mr-2.5 w-7 h-7 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-800">{headerTitle}</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-100">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="flex flex-grow overflow-hidden">
        <div className="w-2/5 border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('chat.searchPeople')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {isLoadingContacts && <p className="p-4 text-sm text-slate-500">{t('chat.loadingContacts')}</p>}
            {contactsError && <p className="p-4 text-sm text-red-600">{contactsError}</p>}
            {!isLoadingContacts && !contactsError && filteredContacts.length === 0 && searchQuery && (
              <p className="p-4 text-sm text-slate-500">{t('chat.noPeopleFound')}</p>
            )}
            {!isLoadingContacts && !contactsError && contacts.length === 0 && !searchQuery && (
              <p className="p-4 text-sm text-slate-500">{t('chat.noContacts')}</p>
            )}
            {filteredContacts.map((contact) => (
              <div key={contact.id} onClick={() => handleUserSelect(contact)} className={`px-4 py-3.5 cursor-pointer border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedUser?.id === contact.id ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''}`}>
                <div className="flex items-center flex-1 min-w-0">
                  {contact.unread > 0 && (
                    <div className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 animate-pulse">
                      {contact.unread}
                    </div>
                  )}
                  <p className={`text-sm font-medium truncate ${selectedUser?.id === contact.id ? 'text-cyan-700' : 'text-slate-800'}`}>
                    {contact.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="w-3/5 flex flex-col bg-slate-50">
          {selectedUser ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200 bg-white">
                <div className="flex items-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{selectedUser.name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {selectedUser.type === 'teacher' ? t('chat.teacher') : 
                       selectedUser.type === 'student' ? t('chat.student') : 
                       (currentUser?.userRole === 'teacher' ? t('chat.student') : t('chat.teacher'))}
                    </p>
                  </div>
                </div>
              </div>
              
              <div 
                ref={dropZoneRef}
                className={`flex-grow flex flex-col overflow-hidden relative ${isDragging ? 'bg-cyan-50 border-4 border-dashed border-cyan-400' : ''}`}
              >
                {isDragging && (
                  <div className="absolute inset-0 bg-cyan-50 bg-opacity-90 flex items-center justify-center z-50 pointer-events-none">
                    <div className="text-center">
                      <ArrowUpTrayIcon className="w-16 h-16 text-cyan-600 mx-auto mb-4 animate-bounce" />
                      <p className="text-2xl font-bold text-cyan-700 mb-2">{t('chat.dropZone.title')}</p>
                      <p className="text-lg text-cyan-600">{t('chat.dropZone.subtitle')}</p>
                      <p className="text-sm text-slate-500 mt-2">{t('chat.dropZone.maxSize')}</p>
                    </div>
                  </div>
                )}
                
                <div 
                  ref={messagesContainerRef}
                  className="flex-grow overflow-y-auto p-4 space-y-3"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(148, 163, 184, 0.5) rgba(241, 245, 249, 0.3)'
                  }}
                >
                  {hasMoreMessages && (
                    <div className="text-center py-2 mb-2">
                      {isLoadingMoreMessages ? (
                        <div className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <button
                          onClick={loadMoreMessages}
                          className="text-xs text-cyan-500 hover:text-cyan-600 transition-colors"
                        >
                          {t('chat.loadOlderMessages')}
                        </button>
                      )}
                    </div>
                  )}

                  {isLoadingMessages && <p className="text-sm text-slate-500 text-center">{t('chat.loadingMessages')}</p>}
                  {messagesError && <p className="text-sm text-red-600 text-center">{messagesError}</p>}
                  {!isLoadingMessages && chatMessages.length === 0 && <p className="text-sm text-slate-500 text-center">{t('chat.noMessages')}</p>}
                  
                  {renderMessagesWithDates()}
                  <div ref={messagesEndRef} />
                </div>
                
                {sendMessageError && <p className="px-4 pb-2 text-xs text-red-600">{sendMessageError}</p>}
                
                {attachedFile && (
                  <div className="mx-3 mb-2 p-2 bg-slate-100 rounded-lg flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-slate-600 mr-2" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{attachedFile.name}</p>
                      <p className="text-xs text-slate-500">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button
                      onClick={removeAttachedFile}
                      className="ml-2 p-1 text-slate-400 hover:text-slate-600"
                    >
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {isUploadingFile && (
                  <div className="mx-3 mb-2">
                    <div className="bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{t('chat.uploadingProgress', { progress: uploadProgress })}</p>
                  </div>
                )}
                
                <div className="p-3 border-t border-slate-200 bg-white">
                  <div className="flex items-end gap-2">
                    <button 
                      onClick={handleAttachmentClick} 
                      className="p-1.5 text-slate-400 hover:text-cyan-600 rounded hover:bg-slate-50 transition-colors flex-shrink-0"
                      disabled={isUploadingFile || isSendingMessage}
                      title={t('chat.attachFile')}
                    >
                      <PaperClipIcon className="w-4 h-4" />
                    </button>
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      style={{ display: 'none' }} 
                      onChange={handleFileInputChange}
                      accept="*/*"
                    />
                    <input
                      type="text"
                      value={messageInput}
                      onChange={handleMessageInputChange}
                      onKeyPress={handleKeyPress}
                      onPaste={handlePaste}
                      placeholder={t('chat.typeMessage')}
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      disabled={isSendingMessage || isUploadingFile}
                      style={{ minHeight: '38px' }}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={(!messageInput.trim() && !attachedFile) || isSendingMessage || isUploadingFile}
                      className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                        (messageInput.trim() || attachedFile) && !isSendingMessage && !isUploadingFile 
                          ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-sm' 
                          : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      }`}
                      title={t('chat.sendMessage')}
                    >
                      <ArrowUpIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-grow flex items-center justify-center text-slate-500 text-sm">
              {t('chat.selectContact')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const MobileView = (
    <div 
      className="fixed inset-0 z-[60] sm:hidden flex flex-col bg-white"
      style={{ height: '100dvh' }}
      data-chat-viewing-user-id={selectedUser?.id || ''}
    >
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          {!showUserList && selectedUser && (
            <button onClick={handleBackToUsers} className="mr-2 text-slate-500 p-1">
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
          )}
          <div className="bg-cyan-500 rounded-full mr-2.5 w-7 h-7 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-800">
            {showUserList ? headerTitle : (selectedUser?.name || t('chat.title'))}
          </h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-100">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      {showUserList ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 p-3 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('chat.searchPeople')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {isLoadingContacts && <p className="p-4 text-sm text-slate-500 text-center">{t('chat.loadingContacts')}</p>}
            {contactsError && <p className="p-4 text-sm text-red-600 text-center">{contactsError}</p>}
            {!isLoadingContacts && !contactsError && filteredContacts.length === 0 && searchQuery && (
              <p className="p-4 text-sm text-slate-500 text-center">{t('chat.noPeopleFound')}</p>
            )}
            {!isLoadingContacts && !contactsError && contacts.length === 0 && !searchQuery && (
              <p className="p-4 text-sm text-slate-500 text-center">{t('chat.noContacts')}</p>
            )}
            {filteredContacts.map((contact) => (
              <div key={contact.id} onClick={() => handleUserSelect(contact)} className={`px-4 py-4 cursor-pointer border-b border-slate-100 hover:bg-slate-50 flex items-center`}>
                {contact.unread > 0 && (
                  <div className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 animate-pulse">
                    {contact.unread}
                  </div>
                )}
                <p className="text-base font-medium text-slate-800 truncate">{contact.name}</p>
              </div>
            ))}
          </div>
        </div>
      ) : selectedUser ? (
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
          <div 
            ref={dropZoneRef}
            className={`flex-1 flex flex-col min-h-0 relative ${isDragging ? 'bg-cyan-50 border-4 border-dashed border-cyan-400' : ''}`}
          >
            {isDragging && (
              <div className="absolute inset-0 bg-cyan-50 bg-opacity-90 flex items-center justify-center z-50 pointer-events-none">
                <div className="text-center">
                  <ArrowUpTrayIcon className="w-16 h-16 text-cyan-600 mx-auto mb-4 animate-bounce" />
                  <p className="text-2xl font-bold text-cyan-700 mb-2">{t('chat.dropZone.title')}</p>
                  <p className="text-lg text-cyan-600">{t('chat.dropZone.subtitle')}</p>
                  <p className="text-sm text-slate-500 mt-2">{t('chat.dropZone.maxSize')}</p>
                </div>
              </div>
            )}
            
            {/* column-reverse: the scroll start is at the bottom by default.
                iOS Safari cannot reliably scroll flex-1 containers to bottom
                programmatically, but column-reverse needs zero scroll logic —
                the container is always "at the bottom" on first render and new
                messages push existing ones up naturally. Messages are rendered
                newest-first (reversed) so they appear in the correct order. */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto px-4"
              style={{
                WebkitOverflowScrolling: 'touch',
                display: 'flex',
                flexDirection: 'column-reverse',
                minHeight: 0,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              {/* Reversed order: newest messages at top of this list = bottom of screen */}
              {!isLoadingMessages && chatMessages.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  {t('chat.noMessagesWith', { name: selectedUser.name })}
                </p>
              )}
              {messagesError && <p className="text-sm text-red-600 text-center py-2">{messagesError}</p>}
              {isLoadingMessages && <p className="text-sm text-slate-500 text-center py-4">{t('chat.loadingMessages')}</p>}
              <div ref={messagesEndRef} />
              {renderMessagesWithDatesReversed()}
              {/* Load older messages button sits at the visual top */}
              {hasMoreMessages && (
                <div className="text-center py-2">
                  {isLoadingMoreMessages ? (
                    <div className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <button
                      onClick={loadMoreMessages}
                      className="text-xs text-cyan-500 hover:text-cyan-600 transition-colors"
                    >
                      {t('chat.loadOlderMessages')}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {sendMessageError && <p className="px-4 pb-2 text-xs text-red-600">{sendMessageError}</p>}
            
            {attachedFile && (
              <div className="flex-shrink-0 mx-3 mb-2 p-2 bg-slate-100 rounded-lg flex items-center">
                <DocumentTextIcon className="w-5 h-5 text-slate-600 mr-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{attachedFile.name}</p>
                  <p className="text-xs text-slate-500">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                </div>
                <button
                  onClick={removeAttachedFile}
                  className="ml-2 p-1 text-slate-400 hover:text-slate-600"
                >
                  <XCircleIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            
            {isUploadingFile && (
              <div className="flex-shrink-0 mx-3 mb-2">
                <div className="bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-600 mt-1">{t('chat.uploadingProgress', { progress: uploadProgress })}</p>
              </div>
            )}
            
            <div ref={inputBarRef} className="flex-shrink-0 p-3 border-t border-slate-200 bg-white safe-area-inset-bottom">
              <div className="flex items-end gap-2">
                <button 
                  onClick={handleAttachmentClick} 
                  className="p-1.5 text-slate-400 hover:text-cyan-600 rounded hover:bg-slate-50 transition-colors flex-shrink-0"
                  disabled={isUploadingFile || isSendingMessage}
                  title={t('chat.attachFile')}
                >
                  <PaperClipIcon className="w-4 h-4" />
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  onChange={handleFileInputChange}
                  accept="*/*"
                />
                <input
                  type="text"
                  value={messageInput}
                  onChange={handleMessageInputChange}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder={t('chat.messagePlaceholder')}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  disabled={isSendingMessage || isUploadingFile}
                  style={{ minHeight: '38px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={(!messageInput.trim() && !attachedFile) || isSendingMessage || isUploadingFile}
                  className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                    (messageInput.trim() || attachedFile) && !isSendingMessage && !isUploadingFile 
                      ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-sm' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                  title={t('chat.sendMessage')}
                >
                  <ArrowUpIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {MobileView}
      {DesktopView}
    </>
  );
}

export default React.memo(ChatWindow);